import React, { useState } from 'react';
import { Card, Input, Button, Tag, Spin, Modal, message, Statistic, Drawer, List, Row, Col, Tooltip, DatePicker } from 'antd';
import { EyeOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined, PrinterOutlined, EditOutlined, FileTextOutlined, CheckCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import dayjs, { Dayjs } from 'dayjs';
import { stockService } from '../../services/stockService';
import api from '../../services/api';
import styles from './StockInPageMobile.module.css';
import StockInPrintModal from '../../components/stock-in/StockInPrintModal';
import StockInModalMobile from '../../components/stock-in/StockInModalMobile';
import { DatePickerModal } from '../../components/common/DatePickerModal';

interface StockInItem {
  id: number;
  medicine_id: number;
  medicine_name: string;
  medicine_code: string;
  quantity: number;
  unit: string;
  unit_price: number;
  lot_number: string;
  expiry_date: string;
  manufacturing_date: string;
  supplier_id: number;
  supplier_name: string;
  total_amount: number;
  notes: string;
}

interface StockInRecord {
  id: number;
  voucher_number: string;
  supplier_id: number;
  supplier_name: string;
  total_items: number;
  total_amount: number;
  status: 'pending' | 'completed' | 'cancelled';
  created_at: string;
  completed_at?: string;
  created_by_name: string;
  notes: string;
  items?: StockInItem[];
}

const StockInPageMobile: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState<string>(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState<string>(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState<StockInRecord | null>(null);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printRecord, setPrintRecord] = useState<StockInRecord | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [datePickerState, setDatePickerState] = useState<{
    isOpen: boolean;
    currentDate: Dayjs | null;
    field: 'dateFrom' | 'dateTo' | null;
  }>({
    isOpen: false,
    currentDate: null,
    field: null
  });
  const queryClient = useQueryClient();

  const { data: stockInData = { data: [], total: 0 }, isLoading } = useQuery(
    ['stock-in', searchText, dateFrom, dateTo],
    () => api.get('/api/stock/in', {
      params: {
        search: searchText,
        start_date: dateFrom,
        end_date: dateTo,
        page: 1,
        pageSize: 1000
      }
    }).then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  );
  const stockInRecords = stockInData.data || [];

  // Lấy chi tiết phiếu nhập kho khi mở Drawer hoặc Modal in phiếu
  const { data: detailRecord, isLoading: isLoadingDetail } = useQuery(
    ['stock-in-detail', drawerRecord?.id],
    () => drawerRecord ? stockService.getStockInVoucherDetail(drawerRecord.id) : null,
    { enabled: !!drawerRecord && drawerVisible }
  );
  const { data: printDetailRecord, isLoading: isLoadingPrint } = useQuery(
    ['stock-in-print', printRecord?.id],
    () => printRecord ? stockService.getStockInVoucherDetail(printRecord.id) : null,
    { enabled: !!printRecord && printModalVisible }
  );

  const { data: medicines = [] } = useQuery(
    'medicines',
    () => api.get('/api/medicines').then(res => res.data.data),
    { staleTime: 10 * 60 * 1000 }
  );
  const { data: suppliers = [] } = useQuery(
    'suppliers',
    () => api.get('/api/suppliers').then(res => res.data.data),
    { staleTime: 10 * 60 * 1000 }
  );

  const { data: editingRecord, isLoading: isLoadingEditing } = useQuery(
    ['stock-in-edit', editingId],
    () => editingId ? stockService.getStockInVoucherDetail(editingId) : null,
    { enabled: !!editingId && modalVisible }
  );

  const deleteMutation = useMutation(
    (id: number) => stockService.deleteStockInVoucher(id),
    {
      onSuccess: () => {
        message.success('Xóa phiếu nhập kho thành công!');
        queryClient.invalidateQueries('stock-in');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi xóa phiếu nhập kho');
      },
    }
  );

  const completeMutation = useMutation(
    (id: number) => stockService.completeStockInVoucher(id),
    {
      onSuccess: () => {
        message.success('Hoàn thành nhập kho thành công!');
        queryClient.invalidateQueries('stock-in');
        queryClient.invalidateQueries('inventory');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.details || error.response?.data?.message || 'Có lỗi xảy ra khi hoàn thành nhập kho');
      },
    }
  );

  const handleAdd = () => {
    setEditingId(null);
    setModalVisible(true);
  };

  const handleEdit = (record: StockInRecord) => {
    setEditingId(record.id);
    setModalVisible(true);
  };

  const handleView = (record: StockInRecord) => {
    setDrawerRecord(record);
    setDrawerVisible(true);
  };

  const handlePrint = (record: StockInRecord) => {
    setPrintRecord(record);
    setPrintModalVisible(true);
  };

  const handleComplete = (id: number) => {
    Modal.confirm({
      title: 'Xác nhận hoàn thành nhập kho',
      content: 'Bạn có chắc chắn muốn hoàn thành phiếu nhập kho này? Hành động này sẽ cập nhật tồn kho.',
      okText: 'Hoàn thành',
      cancelText: 'Hủy',
      onOk: () => completeMutation.mutate(id),
    });
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Xác nhận xóa phiếu nhập kho',
      content: 'Bạn có chắc chắn muốn xóa phiếu nhập kho này? Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const openDatePicker = (field: 'dateFrom' | 'dateTo', currentDate?: string) => {
    setDatePickerState({
      isOpen: true,
      currentDate: currentDate ? dayjs(currentDate) : null,
      field
    });
  };

  const closeDatePicker = () => {
    setDatePickerState({
      isOpen: false,
      currentDate: null,
      field: null
    });
  };

  const handleDateSelect = (date: Dayjs) => {
    if (datePickerState.field) {
      if (datePickerState.field === 'dateFrom') {
        setDateFrom(date.format('YYYY-MM-DD'));
      } else {
        setDateTo(date.format('YYYY-MM-DD'));
      }
    }
    closeDatePicker();
  };

  const clearFilters = () => {
    setDateFrom(dayjs().startOf('month').format('YYYY-MM-DD'));
    setDateTo(dayjs().endOf('month').format('YYYY-MM-DD'));
  };

  return (
    <div className={styles['stockin-mobile-container']}>
      {/* ===== HEADER ===== */}
      <header className={styles['stockin-mobile-header']}>
        {/* Thanh tìm kiếm */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Input
            className={styles['stockin-mobile-search']}
            style={{ width: '100%', padding: '12px 40px 12px 16px', border: '1px solid #ced4da', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
            placeholder="Tìm kiếm phiếu nhập..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <span style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 18 }}>
            <ReloadOutlined />
          </span>
        </div>

        {/* Bộ lọc ngày */}
        <div style={{ marginBottom: 16 }}>
          {/* Bộ lọc */}
          <Card size="small" style={{ marginBottom: 8 }}>
            <Row gutter={8}>
              <Col span={12}>
                <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Từ ngày</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input 
                    placeholder="Chọn ngày" 
                    value={dateFrom ? dayjs(dateFrom).format('DD/MM/YYYY') : ''}
                    readOnly
                    style={{ flex: 1, fontSize: 12 }}
                  />
                  <Button 
                    type="primary" 
                    icon={<CalendarOutlined />} 
                    onClick={() => openDatePicker('dateFrom', dateFrom)}
                    style={{ padding: '4px 8px', fontSize: 12 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Đến ngày</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input 
                    placeholder="Chọn ngày" 
                    value={dateTo ? dayjs(dateTo).format('DD/MM/YYYY') : ''}
                    readOnly
                    style={{ flex: 1, fontSize: 12 }}
                  />
                  <Button 
                    type="primary" 
                    icon={<CalendarOutlined />} 
                    onClick={() => openDatePicker('dateTo', dateTo)}
                    style={{ padding: '4px 8px', fontSize: 12 }}
                  />
                </div>
              </Col>
            </Row>
          </Card>
        </div>

      </header>
      <Spin spinning={isLoading}>
        <div className={styles['stockin-mobile-list']}>
          {stockInRecords.map((item: StockInRecord) => (
            <div
              key={item.id}
              className={styles['stockin-mobile-card']}
              style={{
                maxWidth: 420,
                width: '100%',
                background: '#fff',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #e5e7eb',
                margin: '0 auto 4px',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#1890ff', textAlign: 'left' }}>{item.voucher_number}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, textAlign: 'left' }}>Nhà cung cấp: {item.supplier_name}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>{dayjs(item.created_at).format('DD/MM/YYYY')}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 15, textAlign: 'left' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <span style={{ color: '#6b7280', textAlign: 'left' }}>Số lượng:</span>
                    <span style={{ fontWeight: 500, color: '#222', textAlign: 'left' }}>{item.total_items}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <span style={{ color: '#6b7280', textAlign: 'left' }}>Tổng tiền:</span>
                    <span style={{ fontWeight: 700, color: '#6366f1', textAlign: 'left' }}>{(item.total_amount || 0).toLocaleString('vi-VN')} VND</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <span style={{ color: '#6b7280', textAlign: 'left' }}>Trạng thái:</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 12px', borderRadius: 999, fontWeight: 600, fontSize: 13, background: item.status === 'pending' ? '#fef9c3' : item.status === 'completed' ? '#dcfce7' : '#fee2e2', color: item.status === 'pending' ? '#b45309' : item.status === 'completed' ? '#15803d' : '#b91c1c', textAlign: 'left' }}>
                      {item.status === 'completed' ? 'Hoàn thành' : item.status === 'pending' ? 'Chờ xử lý' : 'Đã hủy'}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ background: '#f9fafb', padding: '12px 24px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => handleView(item)} className={styles['stockin-mobile-detail-btn']} style={{ padding: 8, borderRadius: '50%', color: '#1890ff', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <EyeOutlined />
                  </button>
                  <button onClick={() => handlePrint(item)} className={styles['stockin-mobile-detail-btn']} style={{ padding: 8, borderRadius: '50%', color: '#6c757d', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <PrinterOutlined />
                  </button>
                  {item.status === 'pending' && (
                    <>
                      <button onClick={e => { e.stopPropagation(); handleEdit(item); }} className={styles['stockin-mobile-detail-btn']} style={{ padding: 8, borderRadius: '50%', color: '#faad14', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                        <EditOutlined />
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleComplete(item.id); }} className={styles['stockin-mobile-detail-btn']} style={{ padding: 8, borderRadius: '50%', color: '#52c41a', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                        <CheckCircleOutlined />
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleDelete(item.id); }} className={styles['stockin-mobile-detail-btn']} style={{ padding: 8, borderRadius: '50%', color: 'red', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                        <DeleteOutlined />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          {stockInRecords.length === 0 && (
            <div className={styles['stockin-mobile-empty']}>Không có phiếu nhập kho nào phù hợp.</div>
          )}
        </div>
      </Spin>

      {/* Drawer xem chi tiết phiếu nhập kho */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileTextOutlined />
            Chi tiết phiếu nhập kho
          </div>
        }
        placement="bottom"
        height="90vh"
        onClose={() => {
          setDrawerVisible(false);
          setDrawerRecord(null);
        }}
        open={drawerVisible}
        className={styles['stockin-mobile-drawer']}
      >
        {isLoadingDetail ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: 16, color: '#888' }}>Đang tải chi tiết...</div>
          </div>
        ) : detailRecord ? (
          <div>
            <Card size="small" style={{ marginBottom: 12 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Statistic
                    title="Mã phiếu"
                    value={detailRecord.voucher.voucher_number}
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Tổng tiền"
                    value={detailRecord.voucher.total_amount}
                    suffix="VNĐ"
                    valueStyle={{ color: '#52c41a' }}
                    formatter={(value) => value?.toLocaleString('vi-VN')}
                  />
                </Col>
              </Row>
            </Card>
            <Card size="small" style={{ marginBottom: 12 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Nhà cung cấp:</strong>
                  </div>
                  <div>{detailRecord.voucher.supplier_name}</div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Trạng thái:</strong>
                  </div>
                  <Tag color={detailRecord.voucher.status === 'completed' ? 'green' : 'orange'}>
                    {detailRecord.voucher.status === 'completed' ? 'Hoàn thành' : 'Chờ xử lý'}
                  </Tag>
                </Col>
              </Row>
            </Card>
            <Card size="small" style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <strong>Danh sách thuốc:</strong>
              </div>
              <List
                dataSource={detailRecord.items || []}
                renderItem={(item: any) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.medicine_name}
                      description={
                        <div>
                          <div>Số lượng: {item.quantity} {item.unit}</div>
                          <div>Đơn giá: {item.unit_price.toLocaleString('vi-VN')} VNĐ</div>
                          <div>Số lô: {item.lot_number}</div>
                          <div>Hạn sử dụng: {dayjs(item.expiry_date).format('DD/MM/YYYY')}</div>
                          {item.manufacturing_date && (
                            <div>Ngày sản xuất: {dayjs(item.manufacturing_date).format('DD/MM/YYYY')}</div>
                          )}
                          {item.notes && (
                            <div>Ghi chú: {item.notes}</div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
            {detailRecord.voucher.notes && (
              <Card size="small">
                <div style={{ marginBottom: 8 }}>
                  <strong>Ghi chú:</strong>
                </div>
                <div>{detailRecord.voucher.notes}</div>
              </Card>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <FileTextOutlined style={{ fontSize: '48px', marginBottom: 16 }} />
            <div>Không tìm thấy thông tin chi tiết phiếu</div>
          </div>
        )}
      </Drawer>

      {/* Modal thêm/sửa phiếu nhập kho */}
      <StockInModalMobile
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        editingRecord={editingId ? editingRecord : null}
        medicines={medicines}
        suppliers={suppliers}
        isLoadingDetail={isLoadingEditing}
      />

      {/* Modal in phiếu nhập kho */}
      <StockInPrintModal
        visible={printModalVisible}
        onCancel={() => {
          setPrintModalVisible(false);
          setPrintRecord(null);
        }}
        data={printDetailRecord}
        isLoading={isLoadingPrint}
      />

      {/* Floating Add Button */}
      <button
        onClick={handleAdd}
        className={styles['stockin-mobile-add-btn']}
        title="Tạo phiếu nhập mới"
      >
        <PlusOutlined />
      </button>

      {/* ===== DATE PICKER MODAL ===== */}
      <DatePickerModal
        isOpen={datePickerState.isOpen}
        onClose={closeDatePicker}
        onSelect={handleDateSelect}
        selectedDate={datePickerState.currentDate || undefined}
        title={`Chọn ngày ${datePickerState.field === 'dateFrom' ? 'bắt đầu' : 'kết thúc'}`}
      />
    </div>
  );
};

export default StockInPageMobile;
